import { MapPin, CheckCircle2 } from "lucide-react";

/**
 * FranceCoverageMap — Carte SVG stylisée de France indiquant nos zones d'intervention.
 * Ici : couverture nationale complète, mise en valeur des 13 régions métropolitaines.
 */
export default function FranceCoverageMap() {
  const regions = [
    "Île-de-France", "Auvergne-Rhône-Alpes", "Provence-Alpes-Côte d'Azur",
    "Occitanie", "Nouvelle-Aquitaine", "Pays de la Loire",
    "Bretagne", "Normandie", "Hauts-de-France",
    "Grand Est", "Bourgogne-Franche-Comté", "Centre-Val de Loire", "Corse",
  ];

  return (
    <section
      data-testid="france-coverage-map"
      className="relative overflow-hidden bg-gradient-to-b from-slate-50 to-white py-20 md:py-24 border-y border-slate-200"
    >
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-12 items-center">
          {/* Left: Text */}
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#10B981]/25 bg-[#10B981]/[0.06] px-3 py-1.5 mb-5">
              <MapPin className="h-3.5 w-3.5 text-[#10B981]" />
              <span className="text-[10px] uppercase tracking-[0.28em] text-[#10B981] font-bold">
                Nos zones d&apos;intervention
              </span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-[1.05] mb-5">
              ECONATION intervient sur <span className="text-[#0055A4]">toute la France métropolitaine</span>.
            </h2>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed mb-8">
              Notre réseau de <strong className="text-slate-900">plus de 200 artisans partenaires RGE</strong> couvre
              l&apos;intégralité du territoire. De Lille à Marseille, de Brest à Strasbourg, un conseiller ECONATION
              vous accompagne partout en France pour votre projet de rénovation énergétique.
            </p>

            {/* Region chips */}
            <div className="flex flex-wrap gap-2">
              {regions.map((r) => (
                <div
                  key={r}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 font-medium"
                >
                  <CheckCircle2 className="h-3 w-3 text-[#10B981]" />
                  {r}
                </div>
              ))}
            </div>

            <div className="mt-8 grid grid-cols-3 gap-4">
              <Stat value="13" label="Régions couvertes" />
              <Stat value="200+" label="Artisans RGE" />
              <Stat value="24h" label="Délai de rappel" />
            </div>
          </div>

          {/* Right: SVG France map */}
          <div className="relative">
            <div className="absolute -inset-8 rounded-3xl bg-gradient-to-br from-[#0055A4]/10 via-transparent to-[#10B981]/10 blur-2xl" />
            <div className="relative rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-lg">
              <FranceSVG />
              <div className="mt-4 flex items-center justify-center gap-6 text-xs text-slate-500">
                <div className="flex items-center gap-1.5">
                  <div className="h-2.5 w-2.5 rounded-full bg-[#10B981]" />
                  <span>Zone couverte</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="h-2.5 w-2.5 rounded-full bg-[#0055A4]" />
                  <span>Agences principales</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{value}</div>
      <div className="text-slate-500 text-[11px] mt-1 uppercase tracking-wide font-semibold">{label}</div>
    </div>
  );
}

/* Simplified France SVG (metropolitan) with 5 major agencies as orange pins */
function FranceSVG() {
  return (
    <svg viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
      <defs>
        <linearGradient id="franceGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#10B981" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#059669" stopOpacity="0.85" />
        </linearGradient>
        <filter id="softShadow">
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.15" />
        </filter>
      </defs>

      {/* France hexagon-ish outline (stylized) */}
      <path
        d="M 200 20
           L 260 40
           L 310 70
           L 340 130
           L 340 200
           L 320 260
           L 280 320
           L 240 360
           L 200 380
           L 160 370
           L 110 340
           L 80 290
           L 60 230
           L 60 170
           L 80 110
           L 120 60
           L 160 30
           Z"
        fill="url(#franceGrad)"
        stroke="#0F172A"
        strokeWidth="2"
        strokeLinejoin="round"
        filter="url(#softShadow)"
      />

      {/* Corsica */}
      <ellipse cx="350" cy="330" rx="12" ry="22" fill="url(#franceGrad)" stroke="#0F172A" strokeWidth="1.5" />

      {/* Agency pins */}
      {[
        { name: "Paris", x: 210, y: 110 },
        { name: "Lille", x: 220, y: 55 },
        { name: "Lyon", x: 240, y: 220 },
        { name: "Marseille", x: 250, y: 305 },
        { name: "Toulouse", x: 175, y: 300 },
      ].map((c) => (
        <g key={c.name}>
          <circle cx={c.x} cy={c.y} r="9" fill="#0055A4" opacity="0.35">
            <animate attributeName="r" values="9;14;9" dur="2.5s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.35;0;0.35" dur="2.5s" repeatCount="indefinite" />
          </circle>
          <circle cx={c.x} cy={c.y} r="5" fill="#0055A4" stroke="#fff" strokeWidth="2" />
          <text
            x={c.x}
            y={c.y - 12}
            textAnchor="middle"
            fill="#0F172A"
            fontSize="11"
            fontWeight="700"
            fontFamily="system-ui, -apple-system"
          >
            {c.name}
          </text>
        </g>
      ))}
    </svg>
  );
}
